import { FEEDBACK_URL, HELP_CENTER_URL } from '@aloft/toolkit-shared';
import { Block, Button, Flex, Icon, Link, Typography } from '@aloft/toolkit-sui';

import { ModuleContainer } from '../../layout/ModuleContainer.tsx';
import { exportQAResultsPdf } from '../commentary/export/ExportQAResults/exportQAResultsPdf.tsx';
import { ResultsSection } from './components/ResultsSection.tsx';
import { UploadDocumentsSection } from './components/UploadDocumentsSection.tsx';
import { QABetaModuleProps } from './types.ts';

import './QABetaModule.scss';

export const QABetaModule = (props: QABetaModuleProps) => {
  const { address, qaAttempt, startQAMutation } = props;

  const canExport = qaAttempt.status === 'success' && !!qaAttempt.results?.length;

  return (
    <ModuleContainer moduleID="qa-beta" title="Quality Control" compact overflow="hidden">
      <Block height="100%" width="100%" overflow="hidden" className="qa-beta-module">
        <Flex
          height="100%"
          width="100%"
          flexDirection="column"
          paddingY={6}
          paddingX={12}
          gap={4}
          overflowY="auto"
        >
          <Flex gap="32px" className="container">
            <Flex flexDirection="column" gap="16px" className="section">
              <Flex flexDirection="column" gap="16px">
                <Block>
                  <Typography variant="h4">Spend less time on revisions!</Typography>
                  <Typography>
                    Run your report through our QC system to receive feedback within&nbsp;
                    <span style={{ fontWeight: 'bold' }}>3 hours</span> (4am&ndash;9pm PT daily).
                  </Typography>
                </Block>
                <Block>
                  <Typography>How it works:</Typography>
                  {/* TODO: Possibly create SUI `<ol>` and `<li>` components that let us
                                        control `list-style-type`, or audit our existing uses of native list
                                        elements and update our global styles.
                                        In the meantime, pretend like you don't see this. */}
                  <ol
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <li>
                      <Flex gap="4px">
                        <Typography>1.</Typography>
                        <Typography>
                          Upload your report and order documents (we&apos;ll keep the information
                          confidential).
                        </Typography>
                      </Flex>
                    </li>
                    <li>
                      <Flex gap="4px">
                        <Typography>2.</Typography>
                        <Typography>
                          We&apos;ll send you an email when your results are ready.
                        </Typography>
                      </Flex>
                    </li>
                    <li>
                      <Flex gap="4px">
                        <Typography>3.</Typography>
                        <Typography>
                          You decide how to modify your report before submitting.
                        </Typography>
                      </Flex>
                    </li>
                  </ol>
                </Block>
                <Block>
                  <Typography>
                    Check out our&nbsp;
                    <Link href={HELP_CENTER_URL} isExternal>
                      Help Center
                    </Link>
                    &nbsp;and contact us if you need help.
                  </Typography>
                </Block>
              </Flex>
              <Flex flexDirection="column" gap="8px">
                <Typography variant="h2">Upload Documents</Typography>
                <UploadDocumentsSection qaAttempt={qaAttempt} startQAMutation={startQAMutation} />
              </Flex>
            </Flex>
            {qaAttempt.status !== 'idle' && (
              <Flex flexDirection="column" gap="8px" className="section">
                <Flex alignItems="center" gap="32px">
                  <Typography variant="h2">Results</Typography>
                  {canExport && (
                    <Button
                      text="Export"
                      size="sm"
                      onClick={() => {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        exportQAResultsPdf(address, qaAttempt.results!);
                      }}
                    />
                  )}
                </Flex>
                <ResultsSection qaAttempt={qaAttempt} />
                <Flex
                  marginTop="32px"
                  backgroundColor="surface1"
                  paddingX="12px"
                  paddingY="16px"
                  alignItems="center"
                  gap="8px"
                  borderRadius="5px"
                  boxShadow="0px 4px 8px 0px #0000000F"
                >
                  <Icon icon="question-answer" color="textSecondary" />
                  <Typography>
                    Got a revision? Have feedback for our team?&nbsp;
                    <Link isExternal href={FEEDBACK_URL}>
                      Please share with us
                    </Link>
                  </Typography>
                </Flex>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Block>
    </ModuleContainer>
  );
};
